import { Color, Vector2 } from "three";
import { CompositeMode } from "./renderingModes";
const MM_TO_IN = 0.0393701;
// Converts a global position into normalized coordinates relative to the canvas center
export function normalizePosition(x, y, canvas) {
  const canvasBounds = canvas.getBoundingClientRect();
  const canvasX = x - canvasBounds.left;
  const canvasY = y - canvasBounds.top;
  return new Vector2(canvasX / canvasBounds.width * 2 - 1, -(canvasY / canvasBounds.height) * 2 + 1);
}
/**
 * Uses an html image element to load an image
 * @param imageUrl A string to the uri to load the image from
 * @returns HTMLImageElement
 */
export function loadImage(imageUrl) {
  return new Promise((resolve, reject) => {
    const sourceImg = new Image();
    sourceImg.crossOrigin = "Anonymous";
    sourceImg.onload = () => {
      resolve(sourceImg);
    };
    sourceImg.onerror = () => {
      reject(new Error("Could not fetch url: ".concat(imageUrl)));
    };
    sourceImg.src = imageUrl;
  });
}
function buildCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
/**
 * Generates an HTMLCanvasElement of a solid color
 * @param size Width + Height of the canvas to return
 * @param color The hex value of a color
 * @returns HTMLCanvasElement of a solid color
 */
export function colorCanvas(size, color) {
  const canvas = buildCanvas(size.width, size.height);
  const context = canvas.getContext("2d");
  context.fillStyle = color;
  context.fillRect(0, 0, size.width, size.height);
  return canvas;
}
export function compositeImages(imageOne, imageTwo, compositeMode, fillMode) {
  const canvas = buildCanvas(imageOne.width, imageOne.height);
  const context = canvas.getContext("2d");
  context.drawImage(imageOne, 0, 0);
  context.globalCompositeOperation = compositeMode;
  if (fillMode === "tile") {
    context.drawImage(imageTwo, 0, 0);
  } else {
    context.drawImage(imageTwo, 0, 0, imageOne.width, imageOne.height);
  }
  return canvas;
}
// Reset canvases to release memory
// https://pqina.nl/blog/canvas-area-exceeds-the-maximum-limit/
// Width and height has to be 1, not 0 for this to work
export function releaseCanvases(canvases) {
  canvases.forEach(canvas => {
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    ctx && ctx.clearRect(0, 0, 1, 1);
  });
}
export function tileImage(tiledTexture, textureSize, physicalSize, materialImages) {
  const textureDpiX = textureSize.width / (physicalSize.width * MM_TO_IN);
  const textureDpiY = textureSize.height / (physicalSize.height * MM_TO_IN);
  // Otherwise build the tile promise
  const tileElement = materialImages[tiledTexture.sourceUrl];
  const scaleCanvas = document.createElement("canvas");
  // First scale the tile texture based on it's DPI and the target DPI
  let scaleWidth = Math.max(tileElement.naturalWidth * (textureDpiX / tiledTexture.dpi), 1);
  let scaleHeight = Math.max(tileElement.naturalHeight * (textureDpiY / tiledTexture.dpi), 1);
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/canvas#maximum_canvas_size
  const MAX_CANVAS_PIXELS = 16777216;
  if (scaleWidth * scaleHeight > MAX_CANVAS_PIXELS) {
    const scalar = MAX_CANVAS_PIXELS / (scaleWidth * scaleHeight);
    scaleWidth = scaleWidth * scalar;
    scaleHeight = scaleHeight * scalar;
  }
  scaleCanvas.width = scaleWidth;
  scaleCanvas.height = scaleHeight;
  const scaleContext = scaleCanvas.getContext("2d");
  scaleContext.drawImage(tileElement, 0, 0, scaleCanvas.width, scaleCanvas.height);
  // If the tile texture has an overlay color apply that first
  // Otherwise see if the texture has a color and overlay that
  if (tiledTexture.overlayColor) {
    scaleContext.globalCompositeOperation = CompositeMode.overlay;
    scaleContext.fillStyle = "#".concat(new Color(tiledTexture.overlayColor).getHexString());
    scaleContext.fillRect(0, 0, scaleCanvas.width, scaleCanvas.height);
  }
  const tileCanvas = buildCanvas(textureSize.width, textureSize.height);
  const tileContext = tileCanvas.getContext("2d");
  // Tile the scaled texture so it fills the desired texture size
  tileContext.rect(0, 0, textureSize.width, textureSize.height);
  tileContext.fillStyle = tileContext.createPattern(scaleCanvas, "repeat");
  tileContext.fill();
  // Reset
  releaseCanvases([scaleCanvas]);
  return tileCanvas;
}